// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-nocheck

import styled from '@emotion/styled'
import CheckCircle from '@mui/icons-material/CheckCircle'
import Warning from '@mui/icons-material/Warning'
import Button from '@mui/material/Button'
import Checkbox from '@mui/material/Checkbox'
import FormControl from '@mui/material/FormControl'
import FormControlLabel from '@mui/material/FormControlLabel'
import Paper from '@mui/material/Paper'
import TextField from '@mui/material/TextField'
import Typography from '@mui/material/Typography'
import classnames from 'classnames'
import get from 'lodash/get'
import { inject } from 'mobx-react'
import React from 'react'
import { Mutation, Query } from 'react-apollo'
import DateBar from '../../common/date/DateBar'
import withDate from '../../common/date/withDate'
import Loader from '../../common/loader/Loader'
import type { OptionType } from '../../common/select/Select'
import Select from '../../common/select/Select'
import { i18n } from '../index'
import type { StationStore } from '../station/stationTypes'
import css from './EmployeeCard.css'
import { createFeelingMeter, fetchEmployeesList } from './employeeCardQueries'
import { getActiveStationQuestionsQuery } from './FeelingsMeterQuerys'
const FEELINGS = [1, 2, 3, 4, 5]
const MAX_CHARACTERS_FOR_NOTE = 4000
type FeelingsMeterState = {
  feeling: number | null
  feedbackNote: string
  feedbackReceived: string
  feelingIsSelected: boolean
  meterSuccesfullyCreated: boolean
  meterAlreadySubmitted: boolean
  submitting: boolean
  selectedEmployee: number | null
}
type Props = {
  stationStore: StationStore
  date: string
  onDateChange: (...args: Array<any>) => any
}

const FeelingMeterPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  padding: 24px;
  align-items: flex-start;
  width: 640px;
`

@withDate({
  dateFormat: 'YYYY-MM-DD',
})
@inject('stationStore')
class FeelingsMeter extends React.Component<Props, FeelingsMeterState> {
  state = {
    feeling: null,
    feedbackNote: '',
    customQuestions: {},
    feedbackReceived: false,
    feelingIsSelected: false,
    meterSuccesfullyCreated: false,
    meterAlreadySubmitted: false,
    submitting: false,
    selectedEmployee: null,
  }
  questionFieldChanged = (label: string, value: string | number | boolean) => {
    const newState = { ...this.state.customQuestions }
    newState[label].value = value
    this.setState({
      customQuestions: newState,
    })
  }
  feelingsMeterChanged = (label: string, value: string | number | boolean) => {
    const { feeling } = this.state

    if (label === 'feeling') {
      feeling === value
        ? this.setState({
            feelingIsSelected: false,
            feeling: null,
          })
        : this.setState({
            [label]: parseInt(value, 10),
            feelingIsSelected: true,
          })
    } else if (label === 'feedbackNote') {
      if (value.length <= MAX_CHARACTERS_FOR_NOTE) {
        this.setState({
          [label]: value,
        })
      }
    } else {
      this.setState({
        [label]: value,
      })
    }
  }
  createFeelingMeter = async (onMutate: (...args: Array<any>) => any) => {
    const { feeling, feedbackNote, feedbackReceived, selectedEmployee, customQuestions } = this.state
    const { stationStore, date } = this.props
    const stationId = stationStore.getActiveStationId()
    const hasFeedback = feedbackReceived
    const answers = Object.keys(customQuestions)
      .map((key) => {
        const set = customQuestions[key]
        return {
          question: set.question,
          answer: set.value,
        }
      })
      .filter((q) => q.answer)
    const data = {
      date,
      feeling,
      hasFeedback,
      note: feedbackNote,
      answers: answers,
    }
    this.setState({
      submitting: true,
      meterSuccesfullyCreated: false,
      meterAlreadySubmitted: false,
      showNoShift: false,
    })

    try {
      const submit = await onMutate({
        variables: {
          userId: selectedEmployee,
          data,
          stationId,
        },
      })
      const {
        data: { createFeelingMeter },
      } = submit
      this.resetFeedback()
      createFeelingMeter
        ? this.setState({
            meterSuccesfullyCreated: true,
          })
        : this.setState({
            meterAlreadySubmitted: true,
          })
      this.setState({
        submitting: false,
      })
    } catch (error) {
      this.setState({
        submitting: false,
      })
      this.setState({
        showNoShift: true,
      })
      this.resetFeedback()
    }
  }
  resetFeedback = () => {
    const newQuestions = this.state.customQuestions
    const questionKeys = Object.keys(newQuestions)

    if (questionKeys.length > 0) {
      questionKeys.map((key) => {
        return (newQuestions[key].value = '')
      })
    }

    this.setState({
      feelingIsSelected: false,
      feedbackNote: '',
      feeling: null,
      feedbackReceived: false,
      selectedEmployee: null,
      customQuestions: newQuestions,
    })
  }
  onChangeEmployee = (option: OptionType) => {
    this.setState({
      selectedEmployee: option.value,
    })
  }
  submitIsDisabled = () => {
    const { feelingIsSelected, submitting, selectedEmployee } = this.state
    return !feelingIsSelected || submitting || !selectedEmployee
  }

  render() {
    const {
      feeling,
      feedbackNote,
      feedbackReceived,
      feelingIsSelected,
      meterSuccesfullyCreated,
      meterAlreadySubmitted,
      selectedEmployee,
      showNoShift,
      customQuestions,
    } = this.state
    const { date, onDateChange, dateFormat, stationStore } = this.props
    const showWarning = meterAlreadySubmitted && !feelingIsSelected
    const showSuccess = meterSuccesfullyCreated && !feelingIsSelected
    const stationId = stationStore.getActiveStationId()
    return (
      <div className={css.column}>
        <div className={css.feelingsContainer}>
          <Typography variant='h6'>{i18n.t('employeeCard.currentFeeling')}</Typography>
        </div>
        <div className={css.row}>
          <div className={css.column}>
            <DateBar
              {...{
                date,
                dateFormat,
                onDateChange,
              }}
            />
            <Query
              query={fetchEmployeesList}
              variables={{
                stationId,
              }}
            >
              {({ loading, data, error }) => {
                const employees = get(data, 'employees.edges', [])
                const options = employees.map((employee) => ({
                  label: get(employee, 'node.user.name', ''),
                  value: get(employee, 'node.user.id', ''),
                }))
                return (
                  <div className={css.employeeSelector}>
                    <div className={css.employeeSelectorLabel}>{i18n.t('employeeCard.selectEmployee')}</div>
                    <Select
                      options={options}
                      onChange={this.onChangeEmployee}
                      value={selectedEmployee}
                      placeholder={i18n.t('employeeCard.selectEmployeePlaceholder')}
                    />
                  </div>
                )
              }}
            </Query>
          </div>
        </div>
        <div className={css.row}>
          <FeelingMeterPaper>
            <div
              className={classnames(css.feelingsRow, {
                [css.feelingIsSelected]: feelingIsSelected,
              })}
            >
              {FEELINGS.map((item) => (
                <div
                  key={item}
                  className={classnames(css.imageContainer, {
                    [css.selectedFeeling]: item === feeling,
                  })}
                >
                  <img src={`/feeling-${item}.svg`} alt='' onClick={() => this.feelingsMeterChanged('feeling', item)} />
                </div>
              ))}
            </div>
            {showWarning && (
              <span className={css.formAlert}>
                <Warning /> {i18n.t('employeeCard.alreadySubmitted')}
              </span>
            )}
            {showNoShift && (
              <span className={css.formAlert}>
                <Warning /> {i18n.t('employeeCard.userHasNoShift')}
              </span>
            )}
            {showSuccess && (
              <span className={css.formSuccess}>
                <CheckCircle /> {i18n.t('employeeCard.successfullySubmited')}
              </span>
            )}
            <FormControl fullWidth className={css.formFields}>
              <TextField
                id='outlined-multiline-flexible'
                label={i18n.t('employeeCard.freeText')}
                placeholder={i18n.t('employeeCard.feedbackPlaceholder')}
                multiline
                fullWidth
                variant='outlined'
                rows={2}
                margin='normal'
                value={feedbackNote}
                onChange={(e) => this.feelingsMeterChanged('feedbackNote', e.target.value)}
              />
              <Typography className={css.textfieldMargin}>
                {this.state.feedbackNote?.length ?? 0} / {MAX_CHARACTERS_FOR_NOTE}
              </Typography>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={feedbackReceived}
                    onChange={(e) => this.feelingsMeterChanged('feedbackReceived', e.target.checked)}
                    size='large'
                  />
                }
                label={i18n.t('employeeCard.feedback')}
                disableTypography={true}
                sx={{ fontSize: '1.2rem' }}
              />
              <Query
                query={getActiveStationQuestionsQuery}
                variables={{
                  stationId: stationId,
                }}
              >
                {({ loading, error, data }) => {
                  const rawQuestions = get(data, 'getActiveStationQuestionsQuery', [])

                  if (rawQuestions.length && Object.keys(this.state.customQuestions).length === 0) {
                    const questions = rawQuestions.reduce((prev, item, index) => {
                      const question = {
                        [`question_${index}`]: {
                          question: item.question,
                          value: '',
                        },
                      }
                      const newPrev = { ...prev, ...question }
                      return newPrev
                    }, {})
                    this.setState({
                      customQuestions: questions,
                    })
                  }

                  return (
                    <Loader loading={loading} error={error}>
                      {rawQuestions.length !== 0 && (
                        <div>
                          {Object.keys(this.state.customQuestions).map((key, index) => {
                            return (
                              <div key={index}>
                                <h3 className={css.question}>{customQuestions[key].question}</h3>
                                <TextField
                                  id='outlined-multiline-flexible'
                                  placeholder='Vastaus'
                                  multiline
                                  fullWidth
                                  variant='outlined'
                                  maxRows='5'
                                  margin='normal'
                                  value={this.state.customQuestions[key].value}
                                  onChange={(e) => this.questionFieldChanged(key, e.target.value)}
                                />
                              </div>
                            )
                          })}
                        </div>
                      )}
                    </Loader>
                  )
                }}
              </Query>
              <div className={css.saveButtonContainer}>
                <Mutation mutation={createFeelingMeter}>
                  {(onMutate, { loading }) => (
                    <Button
                      color='primary'
                      variant='contained'
                      size='large'
                      className={css.saveButton}
                      disabled={this.submitIsDisabled()}
                      onClick={() => this.createFeelingMeter(onMutate)}
                    >
                      {i18n.t('buttons.save')}
                    </Button>
                  )}
                </Mutation>
              </div>
            </FormControl>
          </FeelingMeterPaper>
        </div>
      </div>
    )
  }
}

export default FeelingsMeter
